import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Dialog, Chip, Menu, MenuItem,
  ListItemIcon, ListItemText, IconButton, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Collapse, TextField, InputAdornment, Button, Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import api from "../components/utils/api";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { formatDate } from "../components/utils/formatoFecha";
import { stylesPdf, sectionTitle, th, td, tdAccent, divider } from "../components/utils/formatoPdf";
import { Delete, Edit } from "@mui/icons-material";
import RemoveRedEyeIcon    from "@mui/icons-material/RemoveRedEye";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import ImageIcon           from "@mui/icons-material/Image";
import SendIcon            from "@mui/icons-material/Send";
import MoreHorizIcon       from "@mui/icons-material/MoreHoriz";
import SearchIcon          from "@mui/icons-material/Search";
import ViewListIcon        from "@mui/icons-material/ViewList";
import ViewModuleIcon      from "@mui/icons-material/ViewModule";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import swal from "sweetalert";
import EditarServicio from "../components/EditarServicio/EditarServicio";
import Loading from "../components/Loading/Loading";
import { useNavigate } from "react-router-dom";
import { returnJsonCrmTurnos, returnJsonCrmOportunidades, returnJsonCrmMaquinas, returnJsonCrmFlag, returnJsonCrmClintes } from "../components/utils/Funciones";
import { obtenerHora } from "../components/utils/Funciones";
import useFetch from "../Hooks/UseFetch";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const LIMIT = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = ["#ED9519","#60a5fa","#a78bfa","#4ade80","#f472b6","#fb923c","#34d399","#e879f9"];
const getAvatarColor  = (name = "") => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
const getInitials     = (name = "") => name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";

const getTipoServicio = (row) => {
  if (row.tipoServicioReparacion === "1") return { label: "Reparacion", bg: "rgba(237,149,25,.12)", color: "#ED9519",  border: "rgba(237,149,25,.3)"  };
  if (row.tipoServicioServicio   === "1") return { label: "Servicio",   bg: "rgba(96,165,250,.1)", color: "#60a5fa",  border: "rgba(96,165,250,.3)"  };
  if (row.tipoServicioGarantia   === "1") return { label: "Garantia",   bg: "rgba(167,139,250,.1)",color: "#a78bfa",  border: "rgba(167,139,250,.3)" };
  if (row.tipoServicioOtros)              return { label: row.tipoServicioOtros.slice(0,12), bg: "rgba(100,100,100,.1)", color: "#9aa0b4", border: "rgba(100,100,100,.2)" };
  return { label: "-", bg: "transparent", color: "#9aa0b4", border: "transparent" };
};

const FILTERS = [
  { id: "todos",          label: "Todos" },
  { id: "pendientes-crm", label: "Pendientes CRM" },
  { id: "enviados-crm",   label: "Enviados CRM" },
  { id: "reparacion",     label: "Reparacion" },
  { id: "servicio",       label: "Servicio" },
  { id: "garantia",       label: "Garantia" },
  { id: "mes",            label: "Este mes" },
];

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, valueColor }) {
  return (
    <Box sx={{
      flex: "1 1 120px", minWidth: 100,
      bgcolor: "background.paper",
      border: "1px solid", borderColor: "divider",
      borderRadius: 2, p: "10px 14px",
    }}>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 500, color: valueColor || "text.primary", lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.disabled", mt: 0.3, display: "block" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ─── Fila expandible (Vista B) ────────────────────────────────────────────────
function ExpandableRow({ row, expanded, onToggle, onMenu, onPdf, onImg, navigate, isDark }) {
  const tipo   = getTipoServicio(row);
  const color  = getAvatarColor(row.cliente || "");
  const crm    = row.flagCrm === "1";

  return (
    <React.Fragment>
      <TableRow
        sx={{
          cursor: "pointer",
          bgcolor: expanded ? (isDark ? "rgba(237,149,25,0.06)" : "rgba(237,149,25,0.04)") : "transparent",
          "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(237,149,25,0.04)" },
        }}
        onClick={() => onToggle(row.id)}
      >
        <TableCell sx={{ width: 36, py: 0.8, px: 1 }}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); onToggle(row.id); }}>
            {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 0.8, color: "text.primary", fontSize: 15, fontWeight: 700, fontFamily: "monospace", width: 60 }}>
          #{row.id}
        </TableCell>
        <TableCell sx={{ py: 0.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 28, height: 28, minWidth: 28, borderRadius: "50%", bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#1a0e00" }}>
              {getInitials(row.cliente || "")}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{row.cliente}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{row.tecnico}</Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.8 }}>
          <Box component="span" sx={{ fontSize: 11, fontWeight: 500, px: 1, py: 0.4, borderRadius: 1, bgcolor: tipo.bg, color: tipo.color, border: `1px solid ${tipo.border}`, whiteSpace: "nowrap" }}>
            {tipo.label}
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.8 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDate(row.fecha)}</Typography>
        </TableCell>
        <TableCell sx={{ py: 0.8 }}>
          <Box component="span" sx={{
            fontSize: 11, fontWeight: 500, px: 1, py: 0.4, borderRadius: 1, whiteSpace: "nowrap",
            bgcolor: crm ? "rgba(74,222,128,0.1)"  : "rgba(251,191,36,0.1)",
            color:   crm ? "#4ade80"               : "#fbbf24",
            border: `1px solid ${crm ? "rgba(74,222,128,0.25)" : "rgba(251,191,36,0.25)"}`,
          }}>
            {crm ? "Enviado" : "Pendiente"}
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.8 }} onClick={e => e.stopPropagation()}>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Tooltip title="PDF"><IconButton size="small" onClick={() => onPdf(row)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, width: 26, height: 26 }}><PictureAsPdfIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
            <Tooltip title="Imagenes"><IconButton size="small" onClick={() => navigate(`/servicios/imagenes/${row.id}`)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, width: 26, height: 26 }}><ImageIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
            <Tooltip title="Mas opciones"><IconButton size="small" onClick={(e) => onMenu(e, row)} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1.5, width: 26, height: 26, color: "primary.main", bgcolor: "rgba(237,149,25,0.08)" }}><MoreHorizIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Fila de detalle expandible */}
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ py: 1.5, px: 2, bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(237,149,25,0.03)", borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}>
                {[
                  { label: "Modelo",    value: row.modelo     },
                  { label: "Chasis",    value: row.chasis     },
                  { label: "Motor",     value: row.motor      },
                  { label: "Horometro", value: row.horometro  },
                  { label: "Hodometro", value: row.hodometro  },
                  { label: "Localidad", value: row.localidad  },
                ].filter(f => f.value).map(({ label, value }) => (
                  <Box key={label}>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
              {row.descripcionFalla && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Descripcion de la falla</Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, mt: 0.3 }}>{row.descripcionFalla}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

// ─── Card (Vista C) ───────────────────────────────────────────────────────────
function ServiceCard({ row, onMenu, onPdf, onImg, navigate, isDark }) {
  const tipo  = getTipoServicio(row);
  const color = getAvatarColor(row.cliente || "");
  const crm   = row.flagCrm === "1";
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, "&:hover": { boxShadow: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#1a0e00", flexShrink: 0 }}>
            {getInitials(row.cliente || "")}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{row.cliente}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{row.tecnico}</Typography>
          </Box>
        </Box>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 500, px: 0.8, py: 0.3, borderRadius: 1, bgcolor: crm ? "rgba(74,222,128,0.1)" : "rgba(251,191,36,0.1)", color: crm ? "#4ade80" : "#fbbf24", border: `1px solid ${crm ? "rgba(74,222,128,0.25)" : "rgba(251,191,36,0.25)"}`, whiteSpace: "nowrap", flexShrink: 0 }}>
          {crm ? "Enviado" : "Pendiente"}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 500, px: 0.8, py: 0.3, borderRadius: 1, bgcolor: tipo.bg, color: tipo.color, border: `1px solid ${tipo.border}` }}>{tipo.label}</Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDate(row.fecha)}</Typography>
        <Typography variant="caption" sx={{ color: "text.disabled", ml: "auto" }}>#{row.id}</Typography>
      </Box>
      {row.modelo && <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5, fontSize: 11 }}>{row.modelo} {row.chasis ? `· ${row.chasis}` : ""}</Typography>}
      {row.descripcionFalla && (
        <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontSize: 11, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>Falla: </Box>
          {row.descripcionFalla}
        </Typography>
      )}
      {row.servicio && (
        <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontSize: 11, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>Servicio: </Box>
          {row.servicio}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
        <Tooltip title="PDF"><IconButton size="small" onClick={() => onPdf(row)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, width: 26, height: 26 }}><PictureAsPdfIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
        <Tooltip title="Imagenes"><IconButton size="small" onClick={() => navigate(`/servicios/imagenes/${row.id}`)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, width: 26, height: 26 }}><ImageIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
        <Tooltip title="Mas opciones"><IconButton size="small" onClick={(e) => onMenu(e, row)} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1.5, width: 26, height: 26, color: "primary.main", bgcolor: "rgba(237,149,25,0.08)" }}><MoreHorizIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
      </Box>
    </Paper>
  );
}

// ─── Paginador ────────────────────────────────────────────────────────────────
function Paginador({ page, totalPages, count, onPage }) {
  const start = (page - 1) * LIMIT + 1;
  const end   = Math.min(page * LIMIT, count);
  const pages = [];
  for (let i = 1; i <= Math.min(totalPages, 7); i++) pages.push(i);

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, flexWrap: "wrap", gap: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {count > 0 ? `Mostrando ${start}-${end} de ${count}` : "Sin resultados"}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        <Button size="small" variant="outlined" disabled={page === 1} onClick={() => onPage(page - 1)} sx={{ minWidth: 32, px: 1, fontSize: 12 }}>
          &lArr; Anterior
        </Button>
        {pages.map(p => (
          <Button key={p} size="small" variant={p === page ? "contained" : "outlined"}
            onClick={() => onPage(p)}
            sx={{
              minWidth: 32, px: 0.5, fontSize: 12,
              ...(p === page && { bgcolor: "#ED9519", color: "#1a0e00", borderColor: "#ED9519", "&:hover": { bgcolor: "#c97d0e" } }),
            }}>
            {p}
          </Button>
        ))}
        {totalPages > 7 && <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5 }}>...</Typography>}
        {totalPages > 7 && (
          <Button size="small" variant={page === totalPages ? "contained" : "outlined"}
            onClick={() => onPage(totalPages)}
            sx={{ minWidth: 32, px: 0.5, fontSize: 12, ...(page === totalPages && { bgcolor: "#ED9519", color: "#1a0e00", borderColor: "#ED9519" }) }}>
            {totalPages}
          </Button>
        )}
        <Button size="small" variant="outlined" disabled={page === totalPages} onClick={() => onPage(page + 1)} sx={{ minWidth: 32, px: 1, fontSize: 12 }}>
          Siguiente &rArr;
        </Button>
      </Box>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Servicios() {
  const navigate   = useNavigate();
  const theme      = useTheme();
  const isDark     = theme.palette.mode === "dark";
  const userToken  = localStorage.getItem("user-token");
  const rol        = localStorage.getItem("roles");
  const esAdmin    = rol === "ROLE_ADMIN";

  const UrlServicios        = api.urlAPI + "servicios";
  const Urlflag             = api.urlAPI + "crm/servicios/";
  const UrlCrmOportunidades = api.crmOportunidades;
  const UrlCrmClientes      = api.crmClientes;
  const UrlCrmTurnos        = api.crmTurnos;
  const UrlCrmMaquinas      = api.crmMaquinas;

  // ─── Estado ───────────────────────────────────────────────────────────────
  const [servicios,   setServicios]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [kpis,        setKpis]        = useState({ total: 0, enviados: 0, pendientes: 0, esteMes: 0 });
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [count,       setCount]       = useState(0);
  const [activeFilter,setActiveFilter]= useState("todos");
  const [searchRaw,   setSearchRaw]   = useState("");
  const [search,      setSearch]      = useState("");
  const [view,        setView]        = useState("table");
  const [expanded,    setExpanded]    = useState(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [editMode,    setEditMode]    = useState(false);
  const [menuAnchor,  setMenuAnchor]  = useState(null);
  const [menuRow,     setMenuRow]     = useState(null);

  const { fetchOportunidad, fetchTurnos, fetchMaquinas, fetchFlag, fetchCliente } = useFetch();

  // ─── Debounce search ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchRaw); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchRaw]);

  // ─── Fetch paginado ───────────────────────────────────────────────────────
  const fetchServicios = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, search, filter: activeFilter });
      const res    = await fetch(`${UrlServicios}?${params}`, { headers: { "iron-token": userToken } });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setServicios(json.data);
      setKpis({ total: json.total, enviados: json.enviados, pendientes: json.pendientes, esteMes: json.esteMes });
      setTotalPages(json.totalPages);
      setCount(json.count);
      setExpanded(new Set());
    } catch {
      swal("Error", "No se pudieron cargar los servicios.", "error");
    } finally {
      setLoading(false);
    }
  }, [UrlServicios, userToken, page, search, activeFilter]);

  useEffect(() => { fetchServicios(); }, [fetchServicios]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleFilter  = (f) => { setActiveFilter(f); setPage(1); };
  const handlePage    = (p) => { setPage(p); window.scrollTo(0, 0); };
  const toggleExpand  = (id) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const openMenu  = (e, row) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuRow(row); };
  const closeMenu = ()        => { setMenuAnchor(null); setMenuRow(null); };

  const abrirDetalle = (row, edit) => {
    setEditingData(row);
    setEditMode(edit);
    setIsModalOpen(true);
    closeMenu();
  };

  // ─── Eliminar ─────────────────────────────────────────────────────────────
  const eliminarRegistro = useCallback(async (row) => {
    closeMenu();
    if (row.flagCrm === "1") { swal("Error", "No se pueden eliminar registros enviados al CRM.", "error"); return; }
    const ok = await swal({ title: "Estas seguro?", text: "Se eliminara el registro permanentemente.", icon: "warning", buttons: ["Cancelar","Eliminar"], dangerMode: true });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await fetch(`${UrlServicios}/${row.id}`, { method: "DELETE", headers: { "Content-type": "application/json; charset=UTF-8", "iron-token": userToken } });
      setLoading(false);
      if (res.ok) { swal("Eliminado!", "Registro eliminado correctamente.", "success"); fetchServicios(); }
      else swal("Error", "No se pudo eliminar el registro.", "error");
    } catch { setLoading(false); swal("Error", "Ocurrio un error.", "error"); }
  }, [UrlServicios, userToken, fetchServicios]);

  // ─── Enviar CRM ───────────────────────────────────────────────────────────
  const EnviarCrm = useCallback(async (row) => {
    closeMenu();
    const ok = await swal({ title: "Estas seguro?", text: "Se enviara el registro al CRM.", icon: "warning", buttons: ["Cancelar","Enviar"], dangerMode: true });
    if (!ok) return;
    if (row.flagCrm === "1") { swal("Error!", "El servicio ya fue enviado al CRM.", "error"); return; }
    setLoading(true);
    const optionMaquina = returnJsonCrmMaquinas(row.chasis);
    const { responseMaquina, errorMaquina } = await fetchMaquinas(UrlCrmMaquinas, optionMaquina);
    if (errorMaquina) { swal("Error!", "Error al obtener la maquina.", "error"); setLoading(false); return; }
    const matches = responseMaquina?.data?.data?.[0]?.matches;
    if (!matches?.length) { swal("Oops!", "Chasis no encontrado.", "error"); setLoading(false); return; }
    const vin = matches[0].target_id;
    const { responseCliente, errorCliente } = await fetchCliente(UrlCrmClientes + vin, returnJsonCrmClintes());
    if (errorCliente || !responseCliente.data?.data?.account_id) { swal("Error!", "No se pudo obtener el cliente.", "error"); setLoading(false); return; }
    const account_id = responseCliente.data.data.account_id;
    const { responseOportunidad, errorOportunidad } = await fetchOportunidad(UrlCrmOportunidades, returnJsonCrmOportunidades(row.fecha, row.id, row.servicio, vin, account_id));
    if (errorOportunidad || !responseOportunidad?.data?.data?.id) { swal("Oops!", "Error al crear la oportunidad.", "error"); setLoading(false); return; }
    const { errorTurno } = await fetchTurnos(UrlCrmTurnos, returnJsonCrmTurnos(row, responseOportunidad.data.data.id));
    if (errorTurno) { swal("Oops!", "Error al crear el turno.", "error"); setLoading(false); return; }
    const { errorFlag } = await fetchFlag(Urlflag + row.id, returnJsonCrmFlag());
    setLoading(false);
    if (errorFlag) { swal("Aviso", "Enviado al CRM pero no pudo actualizarse en la app.", "warning"); return; }
    swal("Exito!", "Servicio enviado al CRM correctamente.", "success");
    fetchServicios();
  }, [fetchMaquinas, fetchCliente, fetchOportunidad, fetchTurnos, fetchFlag, UrlCrmMaquinas, UrlCrmClientes, UrlCrmOportunidades, UrlCrmTurnos, Urlflag, fetchServicios]);

  // ─── PDF ──────────────────────────────────────────────────────────────────
  const generarPDF = useCallback(async (rowData) => {
    const imageUrl = require("../Img/IronLogo2.png");
    const tipoServicio =
      rowData.tipoServicioReparacion === "1" ? "Reparacion"
      : rowData.tipoServicioServicio === "1" ? "Servicio"
      : rowData.tipoServicioGarantia === "1" ? "Garantia"
      : (rowData.tipoServicioOtros || "-");

    const repuestosRows = (rowData.repuestosPorServicios || []).map((r, i) => [
      td(r.cantidad, i % 2 === 1), td(r.codigo, i % 2 === 1), td(r.descripcion, i % 2 === 1),
    ]);
    const repuestosTable = repuestosRows.length > 0
      ? { style: "tableExample", table: { widths: [80, 130, "*"], headerRows: 1, body: [[th("Cantidad"), th("Codigo"), th("Descripcion")], ...repuestosRows] }, layout: "lightHorizontalLines" }
      : { text: "Sin repuestos registrados.", fontSize: 9, color: "#888", margin: [0, 4, 0, 4] };

    // Convertir imágenes del servicio a base64
    // Si hay error (CORS, red, formato inválido) se omite la imagen y el PDF se genera igual
    const fotosList = rowData.imagenesPorServicios || [];
    const fotosBase64 = await Promise.all(
      fotosList.map(async (img) => {
        try {
          const res  = await fetch(api.urlImagenes + img.filename);
          if (!res.ok) return null;
          const blob = await res.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror   = () => resolve(null);
            reader.readAsDataURL(blob);
          });
          // Validar que sea un dataURL de imagen reconocible por pdfmake
          if (typeof dataUrl === "string" && /^data:image\/(jpeg|jpg|png|webp|gif);base64,/.test(dataUrl)) {
            return dataUrl;
          }
          return null;
        } catch {
          return null;
        }
      })
    );
    const fotosValidas = fotosBase64.filter(Boolean);

    // Sección de imágenes (2 por fila)
    const fotosSection = fotosValidas.length > 0 ? [
      sectionTitle("Imagenes del servicio"),
      {
        table: {
          widths: ["*", "*"],
          body: Array.from({ length: Math.ceil(fotosValidas.length / 2) }, (_, i) => [
            { image: fotosValidas[i * 2], width: 230, margin: [0, 4, 4, 4] },
            fotosValidas[i * 2 + 1]
              ? { image: fotosValidas[i * 2 + 1], width: 230, margin: [4, 4, 0, 4] }
              : { text: "" },
          ]),
        },
        layout: "noBorders",
        margin: [0, 0, 0, 8],
      },
    ] : [];

    const content = [
      { columns: [
        { stack: [{ text: "Solicitud de servicio tecnico", style: "mainTitle" }, { text: tipoServicio, bold: true, fontSize: 9, color: "#1a0e00", background: "#ED9519", margin: [0, 3, 0, 0] }] },
        { stack: [{ text: `# ${rowData.id}`, style: "formId", alignment: "right" }, { text: formatDate(rowData.fecha), style: "formDate", alignment: "right" }] },
      ], margin: [0, 0, 0, 2] },
      divider(4, 10),
      sectionTitle("Informacion del cliente"),
      { style: "tableExample", table: { widths: ["*", "*", "*", "*"], headerRows: 1, body: [[th("Cliente"), th("Contacto"), th("Direccion"), th("Localidad")], [td(rowData.cliente), td(rowData.contacto, true), td(rowData.direccion), td(rowData.localidad, true)]] }, layout: "lightHorizontalLines" },
      sectionTitle("Informacion de la maquina"),
      { style: "tableExample", table: { widths: [100, 100, 100, "*"], headerRows: 1, body: [[th("Modelo"), th("N Chasis"), th("N Motor"), th("Tipo de servicio")], [td(rowData.modelo), td(rowData.chasis, true), td(rowData.motor), tdAccent(tipoServicio)]] }, layout: "lightHorizontalLines" },
      { style: "tableExample", table: { widths: ["*", "*"], headerRows: 1, body: [[th("Horometro"), th("Hodometro")], [td(rowData.horometro), td(rowData.hodometro, true)]] }, layout: "lightHorizontalLines", margin: [0, 2, 0, 0] },
      sectionTitle("Informe del tecnico"),
      { style: "tableExample", table: { widths: ["*", "*"], headerRows: 1, body: [[th("Descripcion de la falla"), th("Servicio realizado")], [tdAccent(rowData.descripcionFalla || "-"), td(rowData.servicio || "-", true)]] }, layout: "lightHorizontalLines" },
      sectionTitle("Repuestos utilizados"),
      repuestosTable,
      ...fotosSection,
      sectionTitle("Datos del tecnico"),
      { style: "tableExample", table: { widths: ["*", 80, 90, 90], headerRows: 1, body: [[th("Tecnico"), th("Km recorridos"), th("Hora inicio"), th("Hora fin")], [td(rowData.tecnico), td(rowData.kmRecorridos, true), td(obtenerHora(rowData.horaInicio)), td(obtenerHora(rowData.horaFin), true)]] }, layout: "lightHorizontalLines" },
      sectionTitle("Limitacion de responsabilidad", 14),
      { text: "IRON GROUP no responde por reparaciones hechas por fuentes no autorizadas, abuso, accidente, descuido, almacenamiento inapropiado ni uso de piezas no fabricadas o recomendadas por IRON GROUP.", style: "disclaimer", margin: [0, 0, 0, 4] },
      sectionTitle("Conformidad del cliente", 14),
      { style: "tableExample", table: { widths: ["*", "*", "*", "*"], headerRows: 1, body: [[th("Firma cliente"), th("Aclaracion - DNI"), th("Firma tecnico"), th("Tecnico")], [{ image: `${rowData.firmaCliente}`, width: 90, height: 50, alignment: "center", margin: [0, 4, 0, 4] }, { text: `${rowData.aclaracionCliente || ""}\nDNI: ${rowData.dni || ""}`, fontSize: 9, color: "#333", alignment: "center", margin: [4, 10, 4, 4] }, { image: `${rowData.firmaTecnico}`, width: 90, height: 50, alignment: "center", margin: [0, 4, 0, 4], fillColor: "#f5f5f5" }, { text: rowData.tecnico || "", fontSize: 9, color: "#333", alignment: "center", margin: [4, 10, 4, 4], fillColor: "#f5f5f5" }]] }, layout: "lightHorizontalLines" },
    ];

    const doc = {
      pageMargins: [32, 80, 32, 50],
      header: (currentPage) => ({
        margin: [32, 16, 32, 0],
        stack: [
          { columns: [{ image: imageUrl, width: 150, style: "imagenLogo" }, { stack: [{ text: "Km 26351, Colectora Este Panamericana 1611", style: "direccion", alignment: "right" }, { text: "B1611 Don Torcuato, Buenos Aires", style: "direccion", alignment: "right" }, { text: "postventa@iron-group.com  |  www.iron-group.com", style: "direccion", alignment: "right" }] }] },
          divider(6, 0, "#111111", 1.5),
        ],
      }),
      footer: (currentPage, pageCount) => ({
        margin: [32, 8, 32, 0],
        columns: [
          { text: "Iron Group S.A.  ·  www.iron-group.com", fontSize: 7, color: "#aaaaaa" },
          { text: `Pagina ${currentPage} de ${pageCount}`, fontSize: 7, color: "#aaaaaa", alignment: "right" },
        ],
      }),
      content,
      styles: stylesPdf,
      defaultStyle: { font: "Roboto" },
    };

    try {
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        ? pdfMake.createPdf(doc).download()
        : pdfMake.createPdf(doc).open();
    } catch (pdfErr) {
      console.error("Error al generar PDF:", pdfErr);
      swal("Error", "No se pudo generar el PDF. Intenta de nuevo.", "error");
    }
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: "background.default", minHeight: "100vh" }}>

      <Typography variant="h6" sx={{ mb: 2, color: "text.primary" }}>
        Servicios realizados
      </Typography>

      {/* KPIs */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <KpiCard label="Total"          value={kpis.total}     sub="todos los registros" />
        <KpiCard label="Enviados CRM"   value={kpis.enviados}  sub={`${kpis.total ? Math.round(kpis.enviados/kpis.total*100) : 0}% del total`} valueColor="#4ade80" />
        <KpiCard label="Pendientes CRM" value={kpis.pendientes} sub="sin enviar"          valueColor="#fbbf24" />
        <KpiCard label="Este mes"       value={kpis.esteMes}   sub="servicios del mes"   valueColor="#ED9519" />
      </Box>

      {/* Filtros + toggle de vista */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {FILTERS.map(f => (
            <Chip
              key={f.id}
              label={f.label}
              size="small"
              onClick={() => handleFilter(f.id)}
              color={activeFilter === f.id ? "primary" : "default"}
              variant={activeFilter === f.id ? "filled" : "outlined"}
              sx={{ fontSize: 12, borderRadius: 3, cursor: "pointer", ...(activeFilter !== f.id && { borderColor: "divider", color: "text.secondary" }) }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Vista tabla">
            <IconButton size="small" onClick={() => setView("table")}
              sx={{ border: "1px solid", borderColor: view === "table" ? "#ED9519" : "divider", bgcolor: view === "table" ? "rgba(237,149,25,0.1)" : "transparent", borderRadius: 1.5 }}>
              <ViewListIcon sx={{ fontSize: 18, color: view === "table" ? "#ED9519" : "text.secondary" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Vista cards">
            <IconButton size="small" onClick={() => setView("cards")}
              sx={{ border: "1px solid", borderColor: view === "cards" ? "#ED9519" : "divider", bgcolor: view === "cards" ? "rgba(237,149,25,0.1)" : "transparent", borderRadius: 1.5 }}>
              <ViewModuleIcon sx={{ fontSize: 18, color: view === "cards" ? "#ED9519" : "text.secondary" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Buscador */}
      <TextField
        size="small"
        placeholder="Buscar por ID, cliente, tecnico, modelo, chasis..."
        value={searchRaw}
        onChange={e => setSearchRaw(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} /></InputAdornment> }}
        sx={{ mb: 2, width: { xs: "100%", sm: 380 } }}
      />

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#ED9519" }} />
        </Box>
      )}

      {/* Vista tabla (B) */}
      {!loading && view === "table" && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "background.paper", color: "text.secondary", fontWeight: 500, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid", borderColor: "divider" } }}>
                <TableCell sx={{ width: 36 }} />
                <TableCell sx={{ width: 50 }}>#</TableCell>
                <TableCell>Cliente / Tecnico</TableCell>
                <TableCell sx={{ width: 110 }}>Tipo</TableCell>
                <TableCell sx={{ width: 100 }}>Fecha</TableCell>
                <TableCell sx={{ width: 100 }}>CRM</TableCell>
                <TableCell sx={{ width: 110 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servicios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No hay registros con los filtros actuales
                  </TableCell>
                </TableRow>
              ) : servicios.map(row => (
                <ExpandableRow
                  key={row.id}
                  row={row}
                  expanded={expanded.has(row.id)}
                  onToggle={toggleExpand}
                  onMenu={openMenu}
                  onPdf={generarPDF}
                  onImg={() => {}}
                  navigate={navigate}
                  isDark={isDark}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vista cards (C) */}
      {!loading && view === "cards" && (
        servicios.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
            No hay registros con los filtros actuales
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
            {servicios.map(row => (
              <ServiceCard key={row.id} row={row} onMenu={openMenu} onPdf={generarPDF} navigate={navigate} isDark={isDark} />
            ))}
          </Box>
        )
      )}

      {/* Paginador */}
      {!loading && (
        <Paginador page={page} totalPages={totalPages} count={count} onPage={handlePage} />
      )}

      {/* Menu contextual */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => menuRow && abrirDetalle(menuRow, false)}>
          <ListItemIcon><RemoveRedEyeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Ver detalles</ListItemText>
        </MenuItem>
        {esAdmin && [
          <MenuItem key="edit" onClick={() => menuRow && abrirDetalle(menuRow, true)}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>,
          <MenuItem key="crm" onClick={() => menuRow && EnviarCrm(menuRow)}>
            <ListItemIcon><SendIcon fontSize="small" sx={{ color: "#ED9519" }} /></ListItemIcon>
            <ListItemText>Enviar al CRM</ListItemText>
          </MenuItem>,
          <Divider key="div" />,
          <MenuItem key="del" onClick={() => menuRow && eliminarRegistro(menuRow)} sx={{ color: "error.main" }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Eliminar</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* Modal editar/ver */}
      <Dialog open={isModalOpen} onClose={() => { setIsModalOpen(false); fetchServicios(); }} maxWidth="md" fullWidth>
        <EditarServicio
          rowdata={editingData}
          setEditingDataservicios={setEditingData}
          ListarServicios={fetchServicios}
          setIsModalOpen={setIsModalOpen}
          edit={editMode}
        />
      </Dialog>
    </Box>
  );
}
